import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import ErrorBoundary from 'containers/ErrorBoundary';
import Feed from 'containers/Feed';
import GameBalanceReloader from 'containers/GameBalanceReloader';
import { toggleLeaderboardOpen } from 'containers/GamePage/actions';
import GameScoreContainer from 'containers/GameScoreContainer';
import SiteUsageContinuation from 'containers/SiteUsageContinuation';
import CloseButton from 'components/CloseButton';
import ErrorNotificationPage from 'components/ErrorNotificationPage';
import Game from 'components/Game';
import GameChat from 'components/GameChat';
import GameLeaderboardModal from 'components/GameLeaderboardModal';
import GameScoreDisplay from 'components/GameScoreDisplay';
import BackgroundMusic from 'utils/BackgroundMusic';
import RootClasses from 'utils/RootClasses';
import { LG_BREAKPOINT_AND_UP } from 'utils/constants';
import { GET_GAMES_QUERY } from 'utils/graphql/gamingQueries';
import { getGameMaximized, setGameMaximized } from 'utils/localStorage';
import { ERROR_INVALID_GAME } from 'utils/messages';
import { errorShape, gameShape } from 'utils/shapes';
import withMediaQuery from 'utils/withMediaQuery';
import IconMaximize from 'resources/Icon-Maximize.png';
import IconMinimize from 'resources/Icon-Minimize.png';
import LeaderboardIcon from 'resources/Footer-Icon-Leaderboard.png';
import LeaderdBoardIconActive from 'resources/Button-Leaderboard-Active.png';
import { updateLeaderboardOpen } from './actions';
import { selectLeaderboardOpen } from './selectors';
import './GamePage.css';

const TRANSITION_MS = 450;

class GamePage extends Component {
  constructor(props) {
    super(props);

    this.onGameStartError = this.onGameStartError.bind(this);

    this.state = {
      error: undefined,
      gameData: undefined,
      maximized: getGameMaximized(),
      gameLoadId: 0, // temporary until update balance update the active session also
      transitionState: ''
    };

    this.closeLeaderboard = this.closeLeaderboard.bind(this);
    this.setMaximized = this.transitionMaximize.bind(this, true);
    this.setMinimized = this.transitionMaximize.bind(this, false);

    this.transitionTimeout = null;
  }

  componentWillUnmount() {
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }

    this.closeLeaderboard();
  }

  componentWillReceiveProps(nextProps) {
    if (!this.state.gameData && nextProps.gamesData !== this.props.gamesData) {
      const gamesData = nextProps.gamesData;
      const gameList = gamesData && gamesData.games && gamesData.games.games;
      if (gameList) {
        const gameParam = nextProps.match.params.game;
        const gameData =
          gameParam &&
          gameList.find(g => g.name === gameParam || g.slug === gameParam);
        if (gameData) {
          this.setState({ gameData: gameData });
        } else {
          this.setState({ error: ERROR_INVALID_GAME });
        }
      }
    }
  }

  onGameStartError(message) {
    this.setState({
      error: message
    });
  }

  closeLeaderboard() {
    this.props.setLeaderboardOpen(false);
  }

  transitionMaximize(setMaximized) {
    if (this.state.maximized !== setMaximized && !this.state.transitionState) {
      setGameMaximized(setMaximized);
      this.setState({ transitionState: 'in' });
      this.transitionTimeout = setTimeout(() => {
        this.setState({ maximized: setMaximized, transitionState: 'out' });
        this.transitionTimeout = setTimeout(() => {
          this.setState({ transitionState: '' });
          this.transitionTimeout = null;
        }, TRANSITION_MS);
      }, TRANSITION_MS);
    }
  }

  render() {
    const { error, gameData, maximized, transitionState } = this.state;
    const { leaderboardOpen, toggleLeaderboardOpen } = this.props;

    if (error) {
      return <ErrorNotificationPage notificationMessage={error} />;
    } else if (!gameData) {
      return null;
    }

    const shouldDisplayFeed = this.props.matchesBreakpoint;
    const maxClass = maximized ? ' game-maximized' : '';
    const rootClass = maximized ? 'darken game-maximized' : 'darken';
    const gameWrapperClassName = `game-wrapper ${
      shouldDisplayFeed ? '' : 'without-feed'
    }`;
    const rightPanelClassName = `page-right-panel ${
      shouldDisplayFeed ? '' : 'without-feed'
    }`;

    const rightPanel = !maximized && (
      <div className={rightPanelClassName}>
        <ErrorBoundary>
          {shouldDisplayFeed && (
            <>
              <h2 className="sr-only">Community Wall</h2>
              <Feed narrow />
            </>
          )}
        </ErrorBoundary>
        <ErrorBoundary>
          <GameChat
            gameData={gameData}
            room={gameData.name}
            title="Game Chat"
          />
        </ErrorBoundary>
      </div>
    );

    return (
      <div className="game-page">
        <Helmet>
          <title>{gameData.displayName}</title>
        </Helmet>
        <RootClasses className={rootClass} />
        <BackgroundMusic priority={2} options={null} />
        <div
          className={`page-container game-container${maxClass} ${
            shouldDisplayFeed ? '' : 'without-feed'
          }`}
        >
          <h1 className="sr-only">{gameData.displayName}</h1>
          <div className={gameWrapperClassName}>
            <div className="page-left-panel game-left-panel">
              <GameScoreContainer>
                <GameScoreDisplay className={maxClass} suffix="" />
              </GameScoreContainer>
              {!maximized && (
                <div className="game-info-wrapper">
                  <button
                    className="toggle-leaderboard-btn"
                    onClick={toggleLeaderboardOpen}
                    type="button"
                  >
                    <img
                      src={
                        leaderboardOpen
                          ? LeaderdBoardIconActive
                          : LeaderboardIcon
                      }
                      alt={'Open Leaderboard'}
                    />
                  </button>

                  <button
                    type="button"
                    className="maximize-button"
                    alt="Maximize"
                    onClick={this.setMaximized}
                  >
                    <img src={IconMaximize} alt="" />
                  </button>
                  <CloseButton
                    url={{
                      pathname: '/slots',
                      state: { initiator: 'close-game-button' }
                    }}
                  />
                </div>
              )}
              {maximized && (
                <div>
                  <button
                    className="toggle-leaderboard-btn"
                    onClick={toggleLeaderboardOpen}
                    type="button"
                  >
                    <img
                      src={
                        leaderboardOpen
                          ? LeaderdBoardIconActive
                          : LeaderboardIcon
                      }
                      alt={'Open Leaderboard'}
                    />
                  </button>
                  <button
                    type="button"
                    className="minimize-button"
                    alt="Minimize"
                    onClick={this.setMinimized}
                  >
                    <img src={IconMinimize} alt="" />
                  </button>
                  <CloseButton url="/slots" />
                </div>
              )}
              <div className="game-area">
                <ErrorBoundary>
                  <Game
                    onError={this.onGameStartError}
                    game={gameData}
                    gameReloadKey={this.state.gameLoadId}
                  />
                </ErrorBoundary>
                <ErrorBoundary>
                  <GameLeaderboardModal
                    show={leaderboardOpen}
                    gameData={gameData}
                    handleClose={this.closeLeaderboard}
                    maximizedClassSuffix={maximized ? '-max' : ''}
                  />
                </ErrorBoundary>
                <ErrorBoundary>
                  <GameBalanceReloader
                    reloadCompletedCallback={() => {
                      this.setState({ gameLoadId: this.state.gameLoadId + 1 });
                    }}
                  />
                </ErrorBoundary>
                <ErrorBoundary>
                  <SiteUsageContinuation />
                </ErrorBoundary>
              </div>
            </div>
            {rightPanel}
          </div>
        </div>
        <div className={`game-maximize-transition ${transitionState}`} />
      </div>
    );
  }
}

GamePage.propTypes = {
  gamesData: PropTypes.shape({
    games: PropTypes.shape({
      games: PropTypes.arrayOf(gameShape)
    }),
    loading: PropTypes.bool,
    error: errorShape
  })
};

const withGamesData = graphql(GET_GAMES_QUERY, { name: 'gamesData' });

const mapStateToProps = createStructuredSelector({
  leaderboardOpen: selectLeaderboardOpen()
});

const mapDispatchToProps = dispatch => ({
  setLeaderboardOpen: open => dispatch(updateLeaderboardOpen(open)),
  toggleLeaderboardOpen: () => dispatch(toggleLeaderboardOpen())
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const makeResponsive = compose(
  withMediaQuery(LG_BREAKPOINT_AND_UP),
  withConnect,
  withGamesData
);

const ResponsiveGamePage = makeResponsive(GamePage);

export default ResponsiveGamePage;
